import React, { useState } from "react";
import { ClipLoader } from "react-spinners";
import styled from "styled-components";
import { ArticleSection, LoadingSection } from "@domain/lms/article/article";
import { useIsMobile } from "@utils/hooks";
import { ChevronDownIcon } from "@ui/components/icons";
import { row } from "@ui/components/layout";
import { KebabMenu } from "@ui/components/menus/kebabMenu";
import { SimpleMenuContent } from "@ui/components/menus/simpleMenuContent";
import { animatedSkeletonStyle, BiColor } from "@ui/styles/animatedSkeleton";
import { Z } from "@ui/styles/zIndex";
import { useEditorController } from "@ui/pages/lms/article/articleEditorPage/articleEditorController/editorControllerContext";
import { AttachmentSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/attachmentSectionEditor/attachmentSectionEditor";
import { DocumentSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/documentSectionEditor/documentSectionEditor";
import { ImageSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/imageSectionEditor/imageSectionEditor";
import { SubtitleSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/subtitleSectionEditor/subtitleSectionEditor";
import { TextSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/textSectionEditor/textSectionEditor";
import { VideoSectionEditor } from "@ui/pages/lms/article/articleEditorPage/sectionEditor/videoSectionEditor/videoSectionEditor";

interface SectionEditorProps extends React.HTMLAttributes<HTMLDivElement> {
	section: ArticleSection | LoadingSection;
}
export const SectionEditor: React.FC<SectionEditorProps> = ({ section, ...htmlProps }) => {
	const articleController = useEditorController();
	const [collapsed, setCollapsed] = useState(false);
	const [isHovered, setIsHovered] = useState(false);
	const isMobile = useIsMobile();

	return (
		<Container
			{...htmlProps}
			borderless={section.type === "SUBTITLE" || section.type === "IMAGE"}
			onMouseEnter={() => setIsHovered(true)}
			onMouseLeave={() => setIsHovered(false)}
		>
			{section.type === "SUBTITLE" ? (
				<SubtitleSectionEditor section={section} />
			) : section.type === "VIDEO" ? (
				<VideoSectionEditor section={section} collapsed={collapsed} onExtend={() => setCollapsed(false)} />
			) : section.type === "TEXT" ? (
				<TextSectionEditor section={section} collapsed={collapsed} onExtend={() => setCollapsed(false)} />
			) : section.type === "ATTACHMENT" ? (
				<AttachmentSectionEditor section={section} />
			) : section.type === "IMAGE" ? (
				<ImageSectionEditor section={section} collapsed={collapsed} onExtend={() => setCollapsed(false)} />
			) : section.type === "PDF" ? (
				<DocumentSectionEditor section={section} collapsed={collapsed} onExtend={() => setCollapsed(false)} />
			) : (
				<StyledLoadingSection>
					<ClipLoader />
				</StyledLoadingSection>
			)}
			<SectionMenu blur zIndexActive={section.type === "TEXT"} isVisible={isMobile ? true : isHovered}>
				<SimpleMenuContent
					onDelete={() => {
						if (section.type === "IMAGE" || section.type === "ATTACHMENT") {
							articleController.removeAssetById(section.assetId);
						}
						articleController.removeSection(section.id);
					}}
				/>
			</SectionMenu>
			{section.type !== "SUBTITLE" && section.type !== "ATTACHMENT" && (
				<Collapser collapsed={collapsed} onClick={() => setCollapsed(!collapsed)}>
					<ChevronDownIcon />
				</Collapser>
			)}
		</Container>
	);
};

const Container = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "borderless",
})<{ borderless?: boolean }>`
	${({ theme, borderless }) => !borderless && theme.containerStyles.roundedBlockStyle};
	padding: 10px 16px;
	position: relative;
`;

const SectionMenu = styled(KebabMenu)<{ zIndexActive: boolean }>`
	position: absolute;
	right: 8px;
	top: 4px;
	${({ zIndexActive }) => zIndexActive && `z-index: ${Z.over}`};
`;

const Collapser = styled.div.withConfig({
	shouldForwardProp: (prop) => prop !== "collapsed",
})<{ collapsed: boolean }>`
	cursor: pointer;
	position: absolute;
	right: -30px;
	top: 12px;

	svg {
		flex-shrink: 0;
		${({ collapsed }) => !collapsed && "transform: rotate(180deg)"};
	}

	@media (max-width: 900px) {
		right: -20px;
		top: 13px;
		svg {
			width: 15px;
			height: 15px;
		}
	}
`;

const StyledLoadingSection = styled.div<{ biColor?: BiColor }>`
	height: 400px;
	${animatedSkeletonStyle};
	${row(0, "center", "center")};
`;
